'use client'

import katex from 'katex'
import { memo, useEffect, useRef } from 'react'

/**
 * Colour-role macros, so formulas match the diagrams next to them:
 *   \blue{x}  inputs      \green{w}  parameters   \amber{y}  outputs/targets
 *   \pink{L}  loss/grad   \violet{p} probability  \accent{} highlight  \dim{} de-emphasis
 */
const MACROS: Record<string, string> = {
  '\\blue': '\\htmlClass{gd-m-blue}{#1}',
  '\\green': '\\htmlClass{gd-m-green}{#1}',
  '\\amber': '\\htmlClass{gd-m-amber}{#1}',
  '\\pink': '\\htmlClass{gd-m-pink}{#1}',
  '\\violet': '\\htmlClass{gd-m-violet}{#1}',
  '\\accent': '\\htmlClass{gd-m-accent}{#1}',
  '\\dim': '\\htmlClass{gd-m-dim}{#1}',
  '\\R': '\\mathbb{R}',
}

function render(tex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(tex, {
      displayMode,
      throwOnError: false,
      strict: false,
      macros: { ...MACROS },
      trust: ctx => ctx.command === '\\htmlClass',
      output: 'htmlAndMathml',
    })
  } catch {
    return tex
  }
}

/** Inline maths: <M>{'x^2'}</M> */
export const M = memo(function M({ children }: { children: string }) {
  return <span dangerouslySetInnerHTML={{ __html: render(children, false) }} />
})

/** Split at top-level \qquad so side-by-side equations can wrap on narrow screens. */
function splitParts(tex: string): string[] {
  const parts: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < tex.length; i++) {
    const ch = tex[i]
    if (ch === '\\' && (tex[i + 1] === '{' || tex[i + 1] === '}')) i++
    else if (ch === '{') depth++
    else if (ch === '}') depth--
    else if (depth === 0 && tex.startsWith('\\qquad', i) && !/[a-zA-Z]/.test(tex[i + 6] ?? '')) {
      parts.push(tex.slice(start, i))
      start = i + 6
      i += 5
    }
  }
  parts.push(tex.slice(start))
  return parts.map(p => p.trim()).filter(Boolean)
}

/**
 * Shrink each rendered equation (down to 72%) when it's wider than its box,
 * so long formulas fit on phones instead of scrolling sideways.
 */
function useFitMath() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const root = ref.current
    if (!root || typeof ResizeObserver === 'undefined') return
    const fit = () => {
      for (const el of root.querySelectorAll<HTMLElement>('.katex-display')) {
        el.style.fontSize = ''
        const need = el.scrollWidth
        const have = el.clientWidth
        if (need > have + 1) el.style.fontSize = `${Math.max(0.72, (have / need) * 0.98)}em`
      }
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(root)
    return () => ro.disconnect()
  }, [])
  return ref
}

/** Display maths on its own line. Equations separated by \qquad sit side by side and wrap when space runs out. */
export const MB = memo(function MB({ children }: { children: string }) {
  const ref = useFitMath()
  const parts = splitParts(children)
  if (parts.length <= 1) return <div ref={ref} className="gd-mathblock" dangerouslySetInnerHTML={{ __html: render(children, true) }} />
  return (
    <div ref={ref} className="gd-mathblock gd-mathblock-multi">
      {parts.map((part, i) => (
        <div key={i} dangerouslySetInnerHTML={{ __html: render(part, true) }} />
      ))}
    </div>
  )
})
